import { useEffect, useState } from "react";
import { axiosInstance } from "../services/v2/axiosInstance";
import { push } from "../navigation/v2/sales/push";

/**
 * Los globitos rojos de WappChat y WappTeam (Esteban, 2026-10-10: "un lugar visible permanente donde se mire que hay
 * mensajes por contestar, estilo WhatsApp"). Una sola consulta para toda la pestaña; la usan la barra de arriba, el
 * menú, el ícono de la app instalada y el título de la pestaña.
 *
 * Se actualiza: al entrar, con los avisos al instante (wapp-push), cuando la persona abre una conversación (se ve en
 * las llamadas a la API que la marcan leída), al volver a la pestaña y cada minuto de respaldo.
 */
export interface Contadores { wappchat: number | null; wappteam: number; total: number; }

let valor: Contadores = { wappchat: null, wappteam: 0, total: 0 };
const oyentes = new Set<(c: Contadores) => void>();
let activo = false;
let pidiendo = false;
let otraVez = false;
let espera: any = null;
let ultimoWa = 0;
let ultimaCarga = 0;
// Quién soy y si veo bandejas (lo dice el back con los números): los avisos "wa" de clientes ajenos no mueven mis globitos.
let yo = 0;
let conBandejas = true;
// El dock de WappTeam (si está montado) lleva el número de WappTeam con lo que traen los avisos, sin pedir nada (14-oct-2026).
let dockEquipo = false;
export const ponerWappteam = (n: number | null) => {
	dockEquipo = n !== null;
	if (n === null || !activo || valor.wappteam === n) return;
	publicar({ ...valor, wappteam: n, total: (valor.wappchat || 0) + n });
};

/** Para otras pantallas (lista del CRM): mi id y si veo bandejas compartidas. 0 = aún no se sabe. */
export const quienSoy = () => ({ yo, bandejas: conBandejas });

/**
 * ¿Este aviso "wa" puede cambiar mis globitos? Las palomitas nunca; un cliente con dueña que no soy yo, solo si veo
 * bandejas (Caja, Kits...). Sin "o" (back de antes) o sin saber quién soy: por si acaso, sí.
 */
const waMeToca = (d: any) => {
	if (d?.estado) return false;
	if (!d || !("o" in d) || !yo || conBandejas) return true;
	return d.o == null || Number(d.o) === yo;
};

const TITULO = /^\(\d+\+?\)\s+/;

const aplicarTitulo = () => {
	const limpio = document.title.replace(TITULO, "");
	const nuevo = (valor.total ? "(" + (valor.total > 99 ? "99+" : valor.total) + ") " : "") + limpio;
	if (document.title !== nuevo) document.title = nuevo;
};

const aplicarIcono = () => {
	try {
		const n: any = navigator;
		if (valor.total) n.setAppBadge?.(valor.total);
		else n.clearAppBadge?.();
	} catch { /* sin globito en el ícono */ }
};

const publicar = (c: Contadores) => {
	valor = c;
	oyentes.forEach((fn) => fn(c));
	aplicarTitulo();
	aplicarIcono();
};

const cargar = async () => {
	if (!activo) return;
	if (pidiendo) { otraVez = true; return; }
	pidiendo = true;
	try {
		const r = await axiosInstance.get("/app/_contadores");
		ultimaCarga = Date.now();
		if (r.data && Number(r.data.yo)) { yo = Number(r.data.yo); conBandejas = r.data.bandejas !== false; }
		if (activo && r.data) publicar({ wappchat: r.data.wappchat ?? null, wappteam: Number(r.data.wappteam) || 0, total: Number(r.data.total) || 0 });
	} catch { /* sin red: se quedan los de antes */ }
	pidiendo = false;
	if (otraVez) { otraVez = false; pedirEn(300); }
};

/** Junta varios avisos seguidos en una sola consulta. */
const pedirEn = (ms: number) => {
	if (!activo) return;
	if (espera) return;
	espera = setTimeout(() => { espera = null; cargar(); }, ms);
};

// Abrir una conversación (WappChat con leido=1, WappTeam al leer mensajes) o marcarla como no leída cambia el número.
// Se vuelve a contar DESPUÉS de esa llamada aunque ya hubiera una consulta en espera: la que estaba programada por el
// aviso del mensaje nuevo podía salir antes de marcarse leído y dejar el "1" pegado (Esteban, 2026-10-10).
const LEIDO = /\/_events\?.*\bleido=1|\/_noLeido\b|\/_equipo\/\d+(\?|$)/;
axiosInstance.interceptors.response.use((r) => {
	try {
		const url = String(r.config?.url || "");
		// Leer en WappTeam solo puede bajar el número de WappTeam: si ya está en 0 no hay nada que contar. 29-oct-2026: cada
		// mensaje en un canal grande recarga la conversación abierta en decenas de pestañas, y cada una pedía sus contadores.
		const equipoEnCero = /\/_equipo\/\d+(\?|$)/.test(url) && !valor.wappteam;
		if (activo && LEIDO.test(url) && !equipoEnCero) { clearTimeout(espera); espera = null; pedirEn(400); }
	} catch { /* nada */ }
	return r;
});

let quitarPush: (() => void)[] = [];
let ciclo: any = null;
let tituloObs: MutationObserver | null = null;

/** Lo prende la barra de arriba al entrar con sesión; lo apaga al salir. */
export const iniciarContadores = (campusId: number): (() => void) => {
	activo = true;
	const soltar = push.usar(campusId);
	// Un mensaje en un canal grande avisa a cientos a la vez: cada pestaña pregunta en un momento distinto (1.2-2.7 s)
	// para no llegar todas juntas al servidor (12-oct-2026, hora pico).
	const pronto = () => pedirEn(1200 + Math.round(Math.random() * 1500));
	quitarPush = [
		// "equipo": el dock de WappTeam suma el mensaje él mismo; solo sin dock (celular) se pregunta. Que otros lean lo mío
		// ("equipo_visto") no cambia ningún número.
		push.on("equipo", () => { if (!dockEquipo) pronto(); }),
		push.on("avisos_ia", pronto),
		// "conteos" lo manda el servidor SOLO a la dueña cuyos números se movieron (14-oct-2026): al instante, sin adivinar.
		// Desde 16-oct-2026 trae mi sin leer de WappChat (wc): si no veo bandejas se publica tal cual, sin preguntar. Con bandejas
		// (mis números suman más que mi cartera) o con wc null (contadores no listos), se pregunta como antes.
		push.on("conteos", (d) => {
			if (d && d.wc != null && yo && conBandejas === false) {
				// El respaldo de cada 5 min sigue contando desde la última consulta real (ultimaCarga no se toca).
				const wc = Number(d.wc) || 0;
				if (valor.wappchat !== wc) publicar({ ...valor, wappchat: wc, total: wc + valor.wappteam });
				return;
			}
			pedirEn(300);
		}),
		// "wa" llega por cada mensaje y palomita de toda la escuela. Solo le sirve a quien ve bandejas (Caja, Kits...: sus
		// números no van por dueña), y como mucho una consulta cada 10 s. Las demás ya tienen su "conteos".
		push.on("wa", (d) => {
			if (!conBandejas || !waMeToca(d)) return;
			const ahora = Date.now(); const falta = Math.max(1500, 10000 - (ahora - ultimoWa)); ultimoWa = ahora + falta; pedirEn(falta);
		}),
	];
	// Al volver a la pestaña: solo si lo último tiene más de 15 s (cambiar de pestaña es constante en el día).
	const alVolver = () => { if (document.visibilityState === "visible" && Date.now() - ultimaCarga > 15000) pedirEn(200); };
	document.addEventListener("visibilitychange", alVolver);
	// Respaldo por si se perdió un aviso: cada minuto sin conexión al instante, cada 5 con ella.
	ciclo = setInterval(() => {
		if (document.visibilityState === "visible" && Date.now() - ultimaCarga > (push.conectado ? 290000 : 55000)) cargar();
	}, 60000);
	// Otras pantallas cambian el título (WappTeam, WappDocs): se le vuelve a poner el número adelante.
	const t = document.querySelector("title");
	if (t && typeof MutationObserver !== "undefined") {
		tituloObs = new MutationObserver(() => { if (valor.total && !TITULO.test(document.title)) aplicarTitulo(); });
		tituloObs.observe(t, { childList: true, characterData: true, subtree: true });
	}
	cargar();
	return () => {
		activo = false;
		soltar();
		quitarPush.forEach((f) => f());
		quitarPush = [];
		document.removeEventListener("visibilitychange", alVolver);
		clearInterval(ciclo);
		clearTimeout(espera); espera = null;
		tituloObs?.disconnect(); tituloObs = null;
		publicar({ wappchat: null, wappteam: 0, total: 0 });
	};
};

export const useContadores = (): Contadores => {
	const [c, setC] = useState<Contadores>(valor);
	useEffect(() => {
		oyentes.add(setC);
		setC(valor);
		return () => { oyentes.delete(setC); };
	}, []);
	return c;
};

export const textoGlobito = (n: number) => (n > 99 ? "99+" : String(n));
